import { Link } from "react-router-dom";
import { fmtDate, fmtTime, fmtTimeRange, fmtMonthDay } from "@/lib/format";
import {
  Clock, Calendar as CalendarIcon, Megaphone, ListChecks, ClipboardCheck,
  ArrowRight, MapPin, Timer, Rocket, Star, StickyNote, Users, PhoneCall,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

/**
 * Widget registry: every dashboard widget declares an id (matching backend
 * WIDGET_CATALOG), a size hint, and a component. Adding a new widget is a
 * ~30-line addition — extend WIDGET_CATALOG on the backend and add an entry here.
 */

const Card = ({ children, span = 1, testid }) => (
  <div
    data-testid={testid}
    className={`rounded-2xl border border-white/10 bg-[#0F0F0F] overflow-hidden ${
      span === 2 ? "md:col-span-2" : ""
    }`}
  >
    {children}
  </div>
);

const CardHead = ({ icon: Icon, title, subtitle, right }) => (
  <div className="p-4 border-b border-white/5 flex items-center gap-2.5">
    {Icon && (
      <div className="w-8 h-8 rounded-md grid place-items-center bg-gold/10 text-gold border border-gold/20">
        <Icon className="w-4 h-4" />
      </div>
    )}
    <div className="min-w-0 flex-1">
      <div className="font-display text-lg truncate">{title}</div>
      {subtitle && <div className="text-[11px] text-zinc-500 truncate">{subtitle}</div>}
    </div>
    {right}
  </div>
);

const Empty = ({ children }) => (
  <div className="p-6 text-xs text-zinc-500 text-center">{children}</div>
);

/* ---------- Widgets ---------- */

const TodaysScheduleWidget = ({ data }) => {
  const list = data.todays_schedule || [];
  return (
    <Card testid="w-todays_schedule">
      <CardHead icon={Clock} title="Today's Schedule" subtitle={fmtDate(new Date().toISOString().slice(0, 10))} />
      {list.length === 0 ? (
        <Empty>Nothing scheduled today — enjoy the quiet.</Empty>
      ) : (
        <ul className="divide-y divide-white/5">
          {list.map((r) => (
            <li key={r.id} className="p-3 flex items-center gap-3">
              <div className="text-[11px] text-gold font-mono w-24 shrink-0">{fmtTimeRange(r.start_time, r.end_time)}</div>
              <div className="min-w-0 flex-1">
                <div className="text-sm text-zinc-100 truncate">{r.title}</div>
                {r.location && (
                  <div className="text-[11px] text-zinc-500 inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {r.location}</div>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
};

const UpcomingCallsWidget = ({ data }) => {
  const list = data.upcoming_calls || [];
  return (
    <Card testid="w-upcoming_calls">
      <CardHead icon={CalendarIcon} title="Upcoming Calls" subtitle="Next 5 rehearsals" />
      {list.length === 0 ? (
        <Empty>No upcoming rehearsals scheduled.</Empty>
      ) : (
        <ul className="divide-y divide-white/5">
          {list.map((r) => {
            const md = fmtMonthDay(r.date);
            return (
              <li key={r.id} className="p-3 flex items-center gap-3">
                <div className="text-center w-14 shrink-0">
                  <div className="text-[10px] uppercase text-zinc-500">{md.month}</div>
                  <div className="text-lg font-display text-zinc-100 leading-none">{md.day}</div>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-zinc-100 truncate">{r.title}</div>
                  <div className="text-[11px] text-zinc-500">{fmtTimeRange(r.start_time, r.end_time)} · {r.location || "TBD"}</div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
};

const RehearsalCountdownWidget = ({ data }) => {
  const c = data.rehearsal_countdown;
  return (
    <Card testid="w-rehearsal_countdown">
      <CardHead icon={Timer} title="Rehearsal Countdown" subtitle="Days until opening" />
      {!c ? (
        <Empty>Set an opening night to see the countdown.</Empty>
      ) : (
        <div className="p-6 text-center">
          <div className="font-display text-6xl text-gold tabular-nums" data-testid="countdown-days">
            {c.days >= 0 ? c.days : "🎉"}
          </div>
          <div className="mt-2 text-xs text-zinc-400">
            {c.days > 1 ? "days to go" : c.days === 1 ? "day to go" : c.days === 0 ? "Opening night is today" : "Show has opened"}
          </div>
          <div className="mt-1 text-[11px] text-zinc-500">Opens {fmtDate(c.opening_night)}</div>
        </div>
      )}
    </Card>
  );
};

const AnnouncementsWidget = ({ data }) => {
  const list = data.announcements || [];
  return (
    <Card testid="w-announcements">
      <CardHead icon={Megaphone} title="Announcements" right={
        <Link to="/announcements" className="text-[11px] text-gold inline-flex items-center gap-1 hover:underline">See all <ArrowRight className="w-3 h-3" /></Link>
      } />
      {list.length === 0 ? (
        <Empty>No announcements yet.</Empty>
      ) : (
        <ul className="divide-y divide-white/5">
          {list.map((a) => (
            <li key={a.id} className="p-3">
              <div className="text-sm text-zinc-100 truncate">{a.title}</div>
              <div className="text-[11px] text-zinc-500 mt-0.5 line-clamp-2">{a.body}</div>
              <div className="mt-1 text-[10px] text-zinc-600">{a.created_at ? fmtDate(a.created_at) : ""}</div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
};

const PersonalTasksWidget = ({ data }) => {
  const list = data.personal_tasks || [];
  return (
    <Card testid="w-personal_tasks">
      <CardHead icon={ListChecks} title="Personal Tasks" right={
        <Link to="/actionables" className="text-[11px] text-gold inline-flex items-center gap-1 hover:underline">See all <ArrowRight className="w-3 h-3" /></Link>
      } />
      {list.length === 0 ? (
        <Empty>You&apos;re all caught up.</Empty>
      ) : (
        <ul className="divide-y divide-white/5">
          {list.map((t) => (
            <li key={t.id} className="p-3">
              <div className="text-sm text-zinc-100">{t.description}</div>
              {t.due_date && <div className="text-[11px] text-zinc-500 mt-0.5">Due {fmtDate(t.due_date)}</div>}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
};

const AttendanceWidget = ({ data }) => {
  const a = data.attendance || {};
  const total = (a.present || 0) + (a.absent || 0) + (a.late || 0);
  const pct = total ? Math.round(((a.present || 0) / total) * 100) : 0;
  return (
    <Card testid="w-attendance">
      <CardHead icon={ClipboardCheck} title="Attendance" subtitle={a.rehearsal?.title || "Latest rehearsal"} right={
        <Link to="/attendance" className="text-[11px] text-gold inline-flex items-center gap-1 hover:underline">Open <ArrowRight className="w-3 h-3" /></Link>
      } />
      {total === 0 ? (
        <Empty>No attendance recorded yet.</Empty>
      ) : (
        <div className="p-4">
          <div className="text-4xl font-display text-emerald-300 tabular-nums">{pct}%</div>
          <div className="text-[11px] text-zinc-500 mt-1">present rate</div>
          <div className="mt-4 grid grid-cols-3 gap-2 text-center">
            <Chip label="Present" n={a.present || 0} tint="text-emerald-300" />
            <Chip label="Late"    n={a.late    || 0} tint="text-amber-300" />
            <Chip label="Absent"  n={a.absent  || 0} tint="text-red-300" />
          </div>
        </div>
      )}
    </Card>
  );
};
const Chip = ({ label, n, tint }) => (
  <div className="rounded border border-white/10 bg-[#121212] py-2">
    <div className={`text-lg font-display ${tint}`}>{n}</div>
    <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
  </div>
);

const CalendarWidget = ({ data }) => {
  const list = data.calendar || [];
  return (
    <Card testid="w-calendar" span={2}>
      <CardHead icon={CalendarIcon} title="This Week" subtitle="Next 7 days" right={
        <Link to="/calendar" className="text-[11px] text-gold inline-flex items-center gap-1 hover:underline">Full calendar <ArrowRight className="w-3 h-3" /></Link>
      } />
      {list.length === 0 ? (
        <Empty>Nothing scheduled in the next week.</Empty>
      ) : (
        <ul className="divide-y divide-white/5">
          {list.map((r) => {
            const md = fmtMonthDay(r.date);
            return (
            <li key={r.id} className="p-3 flex items-center gap-3">
              <div className="text-center w-14 shrink-0">
                <div className="text-[10px] uppercase text-zinc-500">{md.month}</div>
                <div className="text-lg font-display text-zinc-100 leading-none">{md.day}</div>
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm text-zinc-100 truncate">{r.title}</div>
                <div className="text-[11px] text-zinc-500">{fmtTime(r.start_time)}–{fmtTime(r.end_time)} · {r.location || "TBD"}</div>
              </div>
            </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
};

const QuickActionsWidget = () => {
  const { user } = useAuth();
  const isAdminOrStaff = ["org_admin", "production_staff", "producer", "super_admin"].includes(user?.role);
  const items = [
    { to: "/calendar",       label: "Calendar",       Icon: CalendarIcon },
    { to: "/cast-notes",     label: "Cast Notes",     Icon: StickyNote },
    { to: "/my-schedule",    label: "My Schedule",    Icon: Clock },
    ...(isAdminOrStaff ? [
      { to: "/audition-day", label: "Audition Day",   Icon: Star },
      { to: "/auditions",    label: "Auditions",      Icon: Users },
      { to: "/announcements", label: "Announcements", Icon: Megaphone },
    ] : [
      { to: "/actionables",  label: "My Tasks",       Icon: ListChecks },
      { to: "/contacts",     label: "Contacts",       Icon: PhoneCall },
    ]),
  ];
  return (
    <Card testid="w-quick_actions">
      <CardHead icon={Rocket} title="Quick Actions" />
      <div className="p-4 grid grid-cols-2 sm:grid-cols-3 gap-2">
        {items.map((it) => (
          <Link
            key={it.to}
            to={it.to}
            className="rounded-lg border border-white/10 bg-[#121212] p-3 hover:border-gold/40 hover:bg-gold/5 transition-colors group"
          >
            <it.Icon className="w-4 h-4 text-gold" />
            <div className="mt-2 text-xs text-zinc-200 group-hover:text-white">{it.label}</div>
          </Link>
        ))}
      </div>
    </Card>
  );
};

export const WIDGET_REGISTRY = {
  todays_schedule:     { component: TodaysScheduleWidget,     span: 1 },
  upcoming_calls:      { component: UpcomingCallsWidget,      span: 1 },
  rehearsal_countdown: { component: RehearsalCountdownWidget, span: 1 },
  announcements:       { component: AnnouncementsWidget,      span: 1 },
  personal_tasks:      { component: PersonalTasksWidget,      span: 1 },
  attendance:          { component: AttendanceWidget,         span: 1 },
  calendar:            { component: CalendarWidget,           span: 2 },
  quick_actions:       { component: QuickActionsWidget,       span: 1 },
};
